import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

const links = [
  { href: "#about", label: "About" },
  { href: "#treatments", label: "Treatments" },
  { href: "#price-list", label: "Price List" },
  { href: "#membership", label: "Membership" },
  { href: "#testimonials", label: "Testimonials" },
  { href: "#contact", label: "Contact" },
];

export const Navigation = ({ onBook }) => {
  const [open, setOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const bookRef = useRef(null);

  // Close the booking dropdown on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (bookRef.current && !bookRef.current.contains(e.target)) {
        setBookOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleBookWith = (who) => {
    setBookOpen(false);
    onBook?.(who);
  };

  return (
    <header
      data-testid="site-navigation"
      className="fixed top-0 inset-x-0 z-50 bg-[#161618] text-[#F1EDDE] border-b border-[#2A2A2C]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="nav-logo"
          aria-label="Stacy Fox · Laser & Advanced Aesthetics"
          className="inline-flex flex-col items-center justify-center leading-none select-none"
        >
          <div
            className="flex items-center text-[#F1EDDE] tracking-[0.12em] text-[1.05rem] sm:text-[1.3rem]"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
              letterSpacing: "0.12em",
            }}
          >
            <span>STACY&nbsp;F</span>
            <span className="relative inline-flex items-center justify-center">
              <span>O</span>
              <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center text-[#F1EDDE]"
                style={{ fontSize: "0.45em", transform: "translateY(2%)" }}
              >
                ✦
              </span>
            </span>
            <span>X</span>
          </div>
          <span
            aria-hidden
            className="block h-px w-full bg-[#F1EDDE]/70 my-1"
          />
          <div
            className="text-[#F1EDDE]/90 uppercase whitespace-nowrap text-[0.42rem] sm:text-[0.5rem]"
            style={{
              fontFamily: "'Montserrat', 'Inter', sans-serif",
              letterSpacing: "0.32em",
              fontWeight: 300,
            }}
          >
            Laser &amp; Advanced Aesthetics
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="link-underline text-[0.72rem] uppercase tracking-[0.22em] text-[#F1EDDE] hover:text-[#B6AA96] transition-colors whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative hidden sm:block" ref={bookRef}>
            <button
              onClick={() => setBookOpen((v) => !v)}
              data-testid="nav-book-btn"
              aria-haspopup="menu"
              aria-expanded={bookOpen}
              className="inline-flex items-center gap-2 bg-[#F1EDDE] text-[#161618] px-4 py-2 text-[0.62rem] lg:px-7 lg:py-3 lg:text-[0.72rem] uppercase tracking-[0.22em] font-medium hover:bg-[#E6DFCC] transition-colors whitespace-nowrap"
            >
              Book Consultation
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  bookOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {bookOpen && (
              <div
                data-testid="nav-book-menu"
                role="menu"
                className="absolute right-0 mt-2 min-w-[240px] bg-[#F1EDDE] border border-[#161618] shadow-xl py-2"
              >
                <button
                  onClick={() => handleBookWith("stacy")}
                  data-testid="nav-book-stacy"
                  role="menuitem"
                  className="w-full text-left px-5 py-3 text-[0.7rem] uppercase tracking-[0.22em] text-[#161618] hover:bg-[#E6DFCC] transition-colors"
                >
                  Book with Stacy
                </button>
                <div className="border-t border-[#C9BEA9] my-1" />
                <button
                  onClick={() => handleBookWith("sarah")}
                  data-testid="nav-book-sarah"
                  role="menuitem"
                  className="w-full text-left px-5 py-3 text-[0.7rem] uppercase tracking-[0.22em] text-[#161618] hover:bg-[#E6DFCC] transition-colors"
                >
                  Prescriber Sarah
                </button>
              </div>
            )}
          </div>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex flex-col justify-center items-center gap-[5px] border border-[#F1EDDE]"
            aria-label="Menu"
          >
            <span className={`w-4 h-px bg-[#F1EDDE] transition ${open ? "translate-y-[3px] rotate-45" : ""}`} />
            <span className={`w-4 h-px bg-[#F1EDDE] transition ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#161618] border-t border-[#2A2A2C]"
        >
          <div className="flex flex-col px-6 py-6 gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.22em] text-[#F1EDDE] py-2 hover:text-[#B6AA96]"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-2 grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  setOpen(false);
                  onBook?.("stacy");
                }}
                className="bg-[#F1EDDE] text-[#161618] py-3 text-[0.65rem] uppercase tracking-[0.22em] font-medium hover:bg-[#E6DFCC]"
                data-testid="mobile-book-stacy"
              >
                Book with Stacy
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  onBook?.("sarah");
                }}
                className="bg-[#F1EDDE] text-[#161618] py-3 text-[0.65rem] uppercase tracking-[0.22em] font-medium hover:bg-[#E6DFCC]"
                data-testid="mobile-book-sarah"
              >
                Prescriber Sarah
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
